/**
 * ReactionBar — the lightweight, non-star-rating way to react to a cover.
 * `tone="dark"` is for use on top of a photographic/blurred overlay (the
 * feed card's hover state); `tone="light"` (default) is for cream/card
 * backgrounds like the detail modal.
 */

import React from 'react';
import { motion } from 'framer-motion';
import { cx } from '@/components/site/primitives';
import { REACTIONS } from './constants';
import { formatCount } from './utils/coverHelpers';

export default function ReactionBar({ counts, myReaction, onToggle, size = 'md', tone = 'light', className = '' }) {
  const compact = size === 'sm';
  const dark = tone === 'dark';

  return (
    <div className={cx('flex flex-wrap items-center gap-1', className)} data-testid="cover-club-reaction-bar">
      {REACTIONS.map((r) => {
        const isActive = myReaction === r.key;
        const count = counts[r.key] ?? 0;
        return (
          <motion.button
            key={r.key}
            type="button"
            whileTap={{ scale: 0.85 }}
            animate={isActive ? { scale: [1, 1.22, 1] } : { scale: 1 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            onClick={(e) => { e.stopPropagation(); onToggle(r.key); }}
            aria-pressed={isActive}
            aria-label={r.label}
            title={r.label}
            data-testid={`cover-reaction-${r.key}`}
            className={cx(
              'inline-flex items-center gap-1 rounded-full border px-2 py-1 text-xs font-medium transition-colors',
              compact && 'px-1.5 py-0.5 text-[11px]',
              !dark && (isActive ? 'border-coral bg-coral/12 text-coral' : 'border-line bg-card text-subtle hover:border-coral/40 hover:text-coral'),
              dark && (isActive ? 'border-coral/70 bg-coral text-white shadow-[0_0_0_3px_rgba(204,111,105,0.25)]' : 'border-white/25 bg-transparent text-white/90 hover:border-white/50')
            )}
          >
            <span>{r.emoji}</span>
            {!compact && <span>{formatCount(count)}</span>}
          </motion.button>
        );
      })}
    </div>
  );
}
